import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import * as moment from 'moment';
import { Question } from 'src/app/core/models/questionnaire.model';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { UserService } from 'src/app/core/services/user.service';
import { KycFieldLockService } from 'src/app/core/services/kyc-field-lock.service';
import { AutofillFilter } from '../question-text/question-text.component';
import { QuestionAnswer } from '../question/question.component';
import { FormControl, Validators } from '@angular/forms';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-question-date',
  templateUrl: './question-date.component.html',
  styleUrls: ['./question-date.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class QuestionDateComponent implements OnInit {
  @Input('question') question!: Question;
  @Input() user: any;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  @ViewChild('dateInput') dateInput!: ElementRef<HTMLInputElement>;

  min!: Date;
  max!: Date;
  autofillMode: boolean = false;
  autofillEditable: boolean = false;
  kycLocked: boolean = false;
  userId: string = '';
  formattedDate: string | Date = '';
  dateFormat = 'DD/MM/YYYY';
  showError: boolean = false;
  dateControl = new FormControl<Date | null>(null, []);
  inputMode: string = 'numeric';
  autocomplete: string | null = null;
  private readonly validInputModes = ['none', 'text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'];

  constructor(
    private dynamicService: DynamicService<any>,
    private userService: UserService,
    private kycFieldLock: KycFieldLockService
  ) {}

  ngOnInit(): void {
    const configInputMode = this.question.configuration?.inputmode;
    if (configInputMode && this.validInputModes.includes(configInputMode)) {
      this.inputMode = configInputMode;
    }
    const configAutocomplete = this.question.configuration?.autocomplete;
    if (configAutocomplete) {
      this.autocomplete = configAutocomplete;
    }

    if (!this.question.elective) {
      this.dateControl.addValidators([Validators.required]);
    }

    if (this.question.configuration) {
      const config = this.question.configuration;

      if (KycFieldLockService.lockedField(this.question)) {
        this.applyKycLock(config.autofill);
      } else if (config.autofill && !this.question.answer?.value) {
        this.processAutofill(config.autofill);
      }

      if (config.min) {
        this.min = new Date(config.min);
      }

      if (config.max) {
        this.max = new Date(config.max);
      }
    }

    if (this.question.answer?.value) {
      // 🔧 FIX: Tratar string ISO (YYYY-MM-DD) corretamente para evitar timezone issues
      const answerValue = this.question.answer.value;
      if (typeof answerValue === 'string') {
        // Se já é string ISO, usar moment para parsear sem timezone shift
        const dateValue = moment(answerValue).toDate();
        this.formattedDate = dateValue;
        this.dateControl.setValue(dateValue);
      } else {
        const dateValue = new Date(answerValue as Date);
        this.formattedDate = dateValue;
        this.dateControl.setValue(dateValue);
      }
    }

    this.change();
  }

  private async processAutofill(autofillConfig: any) {
    this.autofillMode = true;
    this.autofillEditable = autofillConfig.editable ?? false;
    await this.fetchCurrentUserId();

    const filters = this.replacePlaceholdersInFilters(autofillConfig.filters);
    const autofillValue = await this.fetchAutofillValue(
      autofillConfig.from,
      autofillConfig.select,
      filters
    );

    if (autofillValue) {
      // 🔧 FIX: Usar moment para extrair apenas a data (YYYY-MM-DD) sem timezone issues
      // Antes usava .add(12, 'hours') como hack, agora extraímos direto a string ISO
      const date = moment(autofillValue);
      const isoDateString = date.format('YYYY-MM-DD');

      // Salvar como string ISO (YYYY-MM-DD) - sem componente de hora
      this.question.answer = { value: isoDateString };
      const dateValue = date.toDate();
      this.formattedDate = dateValue; // Manter Date para display
      this.dateControl.setValue(dateValue);

      this.change();
    }
  }

  /**
   * CPK-5059: com fillInfo.lockWhenKycPassed e KYC aprovado, mostra a data do cadastro
   * (mesmo sobre um rascunho) e trava o campo. Sem trava, segue o autofill de sempre.
   */
  private async applyKycLock(autofillConfig: any) {
    await this.fetchCurrentUserId();
    const lock = await this.kycFieldLock.resolve(this.question, this.userId);

    if (!lock.locked) {
      if (autofillConfig && !this.question.answer?.value) {
        await this.processAutofill(autofillConfig);
      }
      return;
    }

    const date = moment(lock.value);
    this.kycLocked = true;
    this.question.answer = { ...this.question.answer, value: date.format('YYYY-MM-DD') };
    this.formattedDate = date.toDate();
    this.dateControl.setValue(date.toDate());
    this.dateControl.disable({ emitEvent: false });
    this.change();
  }

  private async fetchCurrentUserId() {
    if (this.user) {
      this.userId = this.user.id;
      return;
    }
    const currentUser = await this.userService.getCurrentUser();
    this.userId = currentUser?.id || '';
  }

  private replacePlaceholdersInFilters(
    filters: AutofillFilter[]
  ): AutofillFilter[] {
    return filters.map((filter) => ({
      ...filter,
      val: filter.val.replace('$userId', this.userId),
    }));
  }

  private async fetchAutofillValue(
    from: string,
    select: string,
    filters: AutofillFilter[]
  ): Promise<string | null> {
    try {
      this.dynamicService.load(from);
      const result = await this.dynamicService.autofillQuery(
        from,
        select,
        filters
      );

      return (
        result?.[0]?.[
          (select as any).split('->>')[( select as any).split('->>').length - 1]
        ] || null
      );
    } catch (error) {
      console.error('Erro ao buscar valor de autofill:', error);
      return null;
    }
  }

  change(event?: MatDatepickerInputEvent<moment.Moment>) {
    if (event && event.value) {
      // 🔧 FIX: Salvar como string ISO (YYYY-MM-DD) para evitar problemas de timezone
      // Quando salvamos Date object, o JSON.stringify converte para ISO com hora UTC,
      // o que pode mudar o dia dependendo do timezone do usuário.
      const isoDateString = event.value.format('YYYY-MM-DD');

      this.question.answer = { value: isoDateString };
      this.formattedDate = event.value.toDate(); // Manter Date para display
    } else if (event) {
      this.question.answer = { value: null };
      this.formattedDate = '';
      this.dateControl.setValue(null);
    }
    // Data travada veio do cadastro com KYC aprovado: min/max do questionário não podem prender o cliente.
    this.question.isValid = this.kycLocked || this.checkValidation();

    if (this.question.answer) {
      this.changeAnswer.next({
        answer: this.question.answer,
        question: this.question,
      });
    }
  }

  checkValidation() {
    if (!this.question.answer?.value) {
      return false;
    }

    // 🔧 FIX: Trabalhar com string ISO (YYYY-MM-DD) ou Date
    const answerValue = this.question.answer.value;
    const date = typeof answerValue === 'string'
      ? new Date(answerValue + 'T12:00:00') // Adicionar meio-dia para evitar timezone issues
      : new Date(answerValue as Date);

    const mindate = this.min || new Date(-1899240400000);
    const maxdate = this.max || new Date(1899240400000);

    if (isNaN(date.getTime())) {
      return false;
    }

    return date.getTime() > mindate.getTime() && date.getTime() < maxdate.getTime();
  }

  onDateInput(event: any): void {
    let value: string = event.target.value;

    value = value.replace(/[^0-9]/g, '');

    if (value.length > 8) {
      value = value.substring(0, 8);
    }

    if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length > 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    if (event.target.value !== value) {
      this.dateInput.nativeElement.value = value;
    }

    if (value.length === 10) {
      const date = moment(value, this.dateFormat);
      if (date.isValid()) {
        this.showError = false;
        const isoDateString = date.format('YYYY-MM-DD');

        // 🔧 FIX: Salvar como string ISO (YYYY-MM-DD) para evitar problemas de timezone
        this.question.answer = { value: isoDateString };
        const dateValue = date.toDate();
        this.formattedDate = dateValue;
        this.dateControl.setValue(dateValue);

        this.change();
        this.dateControl.setErrors(null);
      } else {
        this.dateControl.setErrors({ invalidDate: true });
        this.showError = true;
      }
    } else if (value.length === 0) {
      this.question.answer = { value: null };
      this.dateControl.setValue(null);
      this.change();
    }
  }
}
